<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import DropdownMenu from '@/components/dropdown-menu.vue'
import BaseButton from './base-button.vue'
import { type BaseButtonProps, BaseButtonDefaults } from './_constants'
import type { Colors } from '@/shared/types/colors'

defineOptions({ inheritAttrs: false })

type ButtonSecondaryProps = BaseButtonProps & {
  outline?: Colors
  menuAlign?: 'left' | 'right'
  menuIcon?: IconDefinition
}

const props = withDefaults(defineProps<ButtonSecondaryProps>(), {
  ...BaseButtonDefaults,
  menuAlign: 'right'
})

const attrs = useAttrs()

const outlineClass = computed(() => (props.outline ? `btn-secondary-outlined-${props.outline}` : undefined))
const baseButtonProps = computed(() => {
  const { outline, menuAlign, menuIcon, ...rest } = props
  return rest
})

const bindToBaseButton = computed(() => ({
  ...attrs,
  ...baseButtonProps.value
}))
</script>

<template>
  <DropdownMenu v-if="$slots.menu" :align="menuAlign" :class="$style.dropdownWrap">
    <BaseButton
      class="btn-secondary btn-outline-secondary"
      :class="[$style ['button-content'], outlineClass]"
      v-bind="bindToBaseButton"
    >
      <slot />
      <span :class="$style.btnDivider" aria-hidden="true"></span>
      <span :class="$style.caret" aria-hidden="true">
        <FontAwesomeIcon v-if="menuIcon" :icon="menuIcon" />
        <template v-else>▾</template>
      </span>
    </BaseButton>

    <template #items>
      <slot name="menu" />
    </template>
  </DropdownMenu>

  <BaseButton
    v-else
    class="btn-secondary btn-outline-secondary"
    :class="outlineClass"
    v-bind="bindToBaseButton"
  >
    <slot />
  </BaseButton>
</template>

<style module>
.button-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btnDivider {
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.35);
  display: inline-block;
}

.caret {
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.caret :global(svg) {
  width: 12px;
  height: 12px;
}

.dropdownWrap :global(.dropdown-menu) {
  z-index: 2000;
}
</style>
